import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll for navbar
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setScrolled(window.scrollY > 50);
    });
  }

  const faqs = [
    {
      question: 'What is a VPN and why do I need it?',
      answer: 'A VPN (Virtual Private Network) creates a secure, encrypted connection between your device and the internet. It protects your online privacy, secures your data from hackers, and allows you to browse anonymously.'
    },
    {
      question: 'How does GRANDE VPN protect my privacy?',
      answer: 'GRANDE VPN uses military-grade encryption to secure your internet connection. We have a strict zero-log policy, meaning we never track, collect, or share your private data. Your online activities remain completely private.'
    },
    {
      question: 'Can I use GRANDE VPN on multiple devices?',
      answer: 'Yes! GRANDE VPN supports all major platforms including Windows, Mac, iOS, Android, and Linux. You can protect all your devices with a single account.'
    },
    {
      question: 'Will VPN slow down my internet speed?',
      answer: 'GRANDE VPN is optimized for speed. While encryption may cause minimal speed reduction, our global server network and advanced protocols ensure you get the fastest possible connection.'
    },
    {
      question: 'Where are your servers located?',
      answer: 'We operate a global network of high-speed servers across multiple countries and continents, ensuring you always have a fast and reliable connection no matter where you are.'
    },
    {
      question: 'Is it legal to use a VPN?',
      answer: 'Yes, using a VPN is legal in most countries. VPNs are legitimate tools for protecting your privacy and security online. However, using a VPN for illegal activities remains illegal.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img src="https://public.readdy.ai/ai/img_res/c0231bf2-718e-41f2-8c9e-6ffee912c531.png" alt="GRANDE Logo" className="h-10 w-auto" />
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>VPN Features</a>
              <a href="#about" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>About Us</a>
              <a href="#faq" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>FAQ</a>
              <a href="#contact" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Contact</a>
            </div>
            <a href="#contact" className="bg-teal-600 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-teal-700 transition-all whitespace-nowrap cursor-pointer">Get Started</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/8590790d9148f87835ae9edc45f319a2.jpg" 
            alt="Hero Background" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center w-full">
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Secure Your<br />
            <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-teal-500 bg-clip-text text-transparent">Digital Freedom</span>
          </h1>
          <p className="text-xl text-gray-200 mb-10 max-w-2xl mx-auto leading-relaxed">
            Experience true online privacy with military-grade encryption and a global network of secure servers. Browse anonymously, protect your data, and access content freely.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#contact" className="bg-teal-600 text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">Download Now</a>
            <a href="#features" className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-white/20 transition-all border border-white/30 whitespace-nowrap cursor-pointer">Learn More</a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-teal-600 text-xs font-bold uppercase tracking-wider mb-3">WHY CHOOSE US</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Enterprise-Grade VPN Features</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Protect your digital life with advanced security features designed for maximum privacy and performance</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Military-Grade Encryption</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-4">Your data is protected with AES-256 encryption, the same standard used by governments and security experts worldwide to safeguard classified information.</p>
              <a href="#contact" className="text-teal-600 font-medium text-sm hover:text-teal-700 inline-flex items-center cursor-pointer whitespace-nowrap">
                Learn more <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-global-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Global Server Network</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-4">Connect to our worldwide network of high-speed servers strategically located across multiple continents for optimal performance and reliability.</p>
              <a href="#contact" className="text-teal-600 font-medium text-sm hover:text-teal-700 inline-flex items-center cursor-pointer whitespace-nowrap">
                Learn more <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-file-shield-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Zero-Log Policy</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-4">We never track, collect, or share your private data. Your online activities remain completely anonymous and secure with our strict no-logging policy.</p>
              <a href="#contact" className="text-teal-600 font-medium text-sm hover:text-teal-700 inline-flex items-center cursor-pointer whitespace-nowrap">
                Learn more <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-speed-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Lightning-Fast Speed</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-4">Experience blazing-fast connection speeds with our optimized servers and advanced protocols. Stream, download, and browse without any lag or buffering.</p>
              <a href="#contact" className="text-teal-600 font-medium text-sm hover:text-teal-700 inline-flex items-center cursor-pointer whitespace-nowrap">
                Learn more <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-device-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Multi-Device Support</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-4">Protect all your devices with one account. Compatible with Windows, Mac, iOS, Android, and Linux. Secure your entire digital ecosystem effortlessly.</p>
              <a href="#contact" className="text-teal-600 font-medium text-sm hover:text-teal-700 inline-flex items-center cursor-pointer whitespace-nowrap">
                Learn more <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-customer-service-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">24/7 Customer Support</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-4">Our dedicated support team is available around the clock to assist you with any questions or technical issues. We're here to help whenever you need us.</p>
              <a href="#contact" className="text-teal-600 font-medium text-sm hover:text-teal-700 inline-flex items-center cursor-pointer whitespace-nowrap">
                Learn more <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gradient-to-br from-teal-50/50 to-cyan-50/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-teal-600 text-xs font-bold uppercase tracking-wider mb-3">ABOUT GRANDE</p>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight">Protecting Privacy Since Day One</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                At GRANDE d.o.o., we believe that online privacy is a fundamental right. Our mission is to provide individuals and businesses with the tools they need to protect their digital freedom and maintain complete control over their personal information.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Based in Slovenia, we've built our VPN service on the principles of transparency, security, and user privacy. Our team of cybersecurity experts works tirelessly to ensure that your data remains protected against evolving online threats.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                We're committed to maintaining the highest standards of encryption and privacy protection, ensuring that you can browse, stream, and communicate online with complete peace of mind.
              </p>
              <a href="#contact" className="text-teal-600 font-semibold text-base hover:text-teal-700 inline-flex items-center cursor-pointer whitespace-nowrap">
                Our Mission <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            <div className="relative">
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20cybersecurity%20concept%20illustration%20showing%20digital%20shield%20protecting%20network%20connections%20with%20abstract%20geometric%20patterns%20in%20teal%20and%20blue%20colors%2C%20featuring%20encrypted%20data%20streams%20and%20secure%20server%20infrastructure%2C%20professional%20tech%20visualization%20with%20clean%20minimalist%20design%20and%20gradient%20overlay%2C%20corporate%20security%20theme%20with%20interconnected%20nodes%20and%20protection%20symbols&width=600&height=700&seq=about-main-002&orientation=portrait" 
                  alt="Security Network" 
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
              </div>
              <div className="absolute -top-8 -right-8 w-64 h-64 bg-gradient-to-br from-teal-400/20 to-cyan-400/20 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-8 -left-8 w-64 h-64 bg-gradient-to-br from-cyan-400/20 to-teal-400/20 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Everything you need to know about GRANDE VPN</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-gray-200">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-8">{faq.question}</span>
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                    <i className={`ri-${openFaq === index ? 'subtract' : 'add'}-line text-2xl text-teal-600 transition-transform`}></i>
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 pb-6' : 'max-h-0'}`}>
                  <p className="text-base text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-white to-teal-50/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-12">
            <div className="md:col-span-2">
              <p className="text-teal-600 text-xs font-bold uppercase tracking-wider mb-3">GET IN TOUCH</p>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Let's Connect</h2>
              <p className="text-lg text-gray-600 mb-12 leading-relaxed">
                Have questions about GRANDE VPN? Our team is here to help. Reach out to us and we'll get back to you as soon as possible.
              </p>

              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-teal-600"></i>
                  </div>
                  <div className="ml-4">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">EMAIL</p>
                    <p className="text-lg text-gray-900">info@grandedoo.com</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-teal-600"></i>
                  </div>
                  <div className="ml-4">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">PHONE</p>
                    <p className="text-lg text-gray-900">+1 855-408-1360</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-teal-600"></i>
                  </div>
                  <div className="ml-4">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">ADDRESS</p>
                    <p className="text-lg text-gray-900">Ljubljanska cesta 4H<br />1241 Kamnik, Slovenia</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-3">
              <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
                <form 
                  id="contact-form" 
                  data-readdy-form 
                  action="https://readdy.ai/api/form/d4s0fg95bv6k73lc3bvg"
                  method="POST"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const formData = new FormData(form);
                    
                    fetch('https://readdy.ai/api/form/d4s0fg95bv6k73lc3bvg', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                      },
                      body: new URLSearchParams(formData as any).toString(),
                    })
                      .then(response => {
                        if (response.ok) {
                          alert('Message sent successfully! We will get back to you soon.');
                          form.reset();
                        } else {
                          alert('Failed to send message. Please try again.');
                        }
                      })
                      .catch(() => {
                        alert('Failed to send message. Please try again.');
                      });
                  }}
                  className="space-y-6"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">Full Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-base"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-base"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-gray-900 mb-2">Subject</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-base"
                      placeholder="How can we help?"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none text-base"
                      placeholder="Tell us more about your inquiry..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-teal-600 text-white py-4 rounded-lg text-base font-semibold hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                  >
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-cyan-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Start Your Secure Journey Today</h2>
          <p className="text-xl text-white/90 mb-10">Join thousands protecting their privacy with GRANDE VPN</p>
          <a href="#contact" className="inline-block bg-gray-900 text-white px-10 py-4 rounded-xl text-base font-semibold hover:bg-gray-800 transition-all shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer">
            Get GRANDE VPN <i className="ri-arrow-right-line ml-2"></i>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <img src="https://public.readdy.ai/ai/img_res/c0231bf2-718e-41f2-8c9e-6ffee912c531.png" alt="GRANDE Logo" className="h-10 w-auto mb-6" />
              <p className="text-gray-400 text-base leading-relaxed mb-8 max-w-sm">
                Protecting your digital freedom with enterprise-grade security and privacy. Browse safely, stream freely, and stay anonymous online.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 border border-gray-600 rounded-full flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-gray-600 rounded-full flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-gray-600 rounded-full flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-gray-600 rounded-full flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-lg"></i>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider mb-6">Contact Info</h3>
              <div className="space-y-6">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">ADDRESS</p>
                  <p className="text-base text-white">Ljubljanska cesta 4H<br />1241 Kamnik, Slovenia</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">PHONE</p>
                  <p className="text-base text-white">+1 855-408-1360</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">EMAIL</p>
                  <p className="text-base text-white">info@grandedoo.com</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider mb-6">Quick Links</h3>
              <ul className="space-y-4">
                <li><a href="#features" className="text-base text-gray-400 hover:text-teal-500 transition-colors cursor-pointer whitespace-nowrap">VPN Features</a></li>
                <li><a href="#about" className="text-base text-gray-400 hover:text-teal-500 transition-colors cursor-pointer whitespace-nowrap">About Us</a></li>
                <li><a href="#faq" className="text-base text-gray-400 hover:text-teal-500 transition-colors cursor-pointer whitespace-nowrap">FAQ</a></li>
                <li><a href="#contact" className="text-base text-gray-400 hover:text-teal-500 transition-colors cursor-pointer whitespace-nowrap">Contact</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-400 mb-4 md:mb-0">© 2025 GRANDE d.o.o. All rights reserved</p>
            <div className="flex items-center space-x-6">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-teal-500 transition-colors cursor-pointer whitespace-nowrap">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
